import { useState } from 'react';
import { platforms, samplePosts as posts, type PlatformId } from '@/lib/wireframe-data';
import { PlatformIcon } from '@/components/wireframe/ui';

export default function AnalyticsRoute() {
  const [source, setSource] = useState<string>('linkedin');
  const [medium, setMedium] = useState<string>('social');
  const [campaign, setCampaign] = useState<string>('ai-trends');

  const platformCounts: Partial<Record<PlatformId, number>> = {};
  posts.forEach((p) =>
    p.platforms.forEach((pl) => {
      platformCounts[pl] = (platformCounts[pl] ?? 0) + 1;
    }),
  );
  const maxCount = Math.max(1, ...Object.values(platformCounts));
  const scheduledCount = posts.filter((p) => p.status === 'scheduled').length;
  const activity: number[] = [40, 65, 55, 90, 70, 30, 45];
  const dayLabels: string[] = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const kpis: [string, string, string][] = [
    ['Posts tracked', String(156 + posts.length), '+12%'],
    ['Avg. engagement', '4.8%', '+0.6pt'],
    ['Scheduled', String(scheduledCount), 'upcoming'],
    ['Link clicks (UTM)', '2,431', '+18%'],
  ];

  const top: [string, PlatformId, string, string][] = [
    ['5 AI trends reshaping marketing', 'linkedin', '12.4%', '1,204'],
    ['Product launch announcement', 'facebook', '9.1%', '880'],
    ['Weekly tip thread', 'twitter', '6.7%', '512'],
  ];

  const utmString = `?utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;

  return (
    <div className="p-8 space-y-5">
      <h1 className="text-2xl font-semibold text-stone-900">Analytics</h1>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-stone-500">
          Reach, engagement, UTM clicks and AI engagement prediction — across all channels.
        </p>
        <select className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
          <option>Last 7 days</option>
          <option>Last 30 days</option>
          <option>This quarter</option>
        </select>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {kpis.map(([l, v, d]) => (
          <div key={l} className="bg-white border border-stone-200 rounded-xl p-5">
            <p className="text-sm text-stone-500">{l}</p>
            <p className="text-3xl font-bold text-stone-900 mt-1">{v}</p>
            <p className="text-[11px] text-emerald-600 font-semibold mt-1">{d}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h3 className="font-semibold text-stone-900 mb-4">Posts by platform</h3>
          <div className="space-y-3">
            {platforms.map((pl) => {
              const c = platformCounts[pl.id] ?? 0;
              return (
                <div key={pl.id} className="flex items-center gap-3">
                  <PlatformIcon platform={pl.id} />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-stone-600">{pl.name}</span>
                      <span className="font-medium text-stone-700">{c}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-violet-500 rounded-full"
                        style={{ width: `${(c / maxCount) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-stone-400 mt-3">Counts reflect posts in this workspace.</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h3 className="font-semibold text-stone-900 mb-4">Publishing activity</h3>
          <div className="flex items-end justify-between h-32">
            {activity.map((h, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div
                  className="w-7 bg-violet-100 rounded-t hover:bg-violet-200 transition-colors"
                  style={{ height: `${h}%` }}
                />
                <span className="text-[10px] text-stone-400">{dayLabels[i]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h3 className="font-semibold text-stone-900 mb-3">UTM builder</h3>
          <div className="mb-2">
            <label className="text-[11px] font-semibold text-stone-400 uppercase">Source</label>
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-stone-200 rounded-lg text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="mb-2">
            <label className="text-[11px] font-semibold text-stone-400 uppercase">Medium</label>
            <input
              value={medium}
              onChange={(e) => setMedium(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-stone-200 rounded-lg text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="mb-2">
            <label className="text-[11px] font-semibold text-stone-400 uppercase">Campaign</label>
            <input
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-stone-200 rounded-lg text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="rounded-lg bg-stone-50 border border-stone-200 p-2 text-[11px] text-stone-500 break-all mt-2">
            {utmString}
          </div>
          <p className="text-[11px] text-violet-600 font-semibold mt-3">
            ✨ Predicted engagement: High (+25% with a closing question)
          </p>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <h3 className="font-semibold text-stone-900 mb-3">Top posts</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-stone-400 border-b border-stone-100">
              {['Post', 'Channel', 'Engagement', 'Clicks'].map((h) => (
                <th key={h} className="pb-2 font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {top.map(([nm, id, e, c], i) => (
              <tr key={i} className="border-b border-stone-100 last:border-0">
                <td className="py-2.5 font-medium text-stone-800">{nm}</td>
                <td className="py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <PlatformIcon platform={id} />
                    {platforms.find((x) => x.id === id)?.name ?? id}
                  </span>
                </td>
                <td className="py-2.5 text-stone-700">{e}</td>
                <td className="py-2.5 text-stone-500">{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
